import { Alert, Button, Grid, Space, Table, Tabs, Tag, Typography, App as AntApp } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { useAuth } from "../app/auth.tsx";
import { SubjectDrawer } from "../components/SubjectDrawer.tsx";
import { CourseDrawer } from "../components/CourseDrawer.tsx";

type Subject = components["schemas"]["SubjectDetails"];
type Course = components["schemas"]["CourseDetails"];
type RateGroup = components["schemas"]["SubjectRateGroup"];

const rateGroupColor: Record<RateGroup, string> = { Core: "blue", English: "purple", Other: "default" };

function useSubjects() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["academic", "subjects"],
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/subjects");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}

export function SubjectsCoursesPage() {
  const { t } = useTranslation();

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        {t("subjectsCourses.title")}
      </Typography.Title>
      <Tabs
        defaultActiveKey="subjects"
        items={[
          { key: "subjects", label: t("subjects.title"), children: <SubjectsTab /> },
          { key: "courses", label: t("courses.title"), children: <CoursesTab /> },
        ]}
      />
    </Space>
  );
}

function SubjectsTab() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { hasPermission } = useAuth();
  const screens = Grid.useBreakpoint();
  const canManage = hasPermission("academic.manage");
  const subjects = useSubjects();
  const [drawer, setDrawer] = useState<{ open: boolean; subject?: Subject }>({ open: false });

  const columns = [
    { title: t("subjects.name"), dataIndex: "name", key: "name", ellipsis: true },
    {
      title: t("subjects.rateGroup"),
      dataIndex: "rateGroup",
      key: "rateGroup",
      width: 160,
      render: (group: RateGroup) => <Tag color={rateGroupColor[group]}>{t(`subjects.rateGroups.${group}`)}</Tag>,
    },
    ...(canManage
      ? [
          {
            title: t("common.actions"),
            key: "actions",
            width: 70,
            render: (_: unknown, subject: Subject) => (
              <Button type="text" icon={<EditOutlined />} aria-label={t("common.edit")} onClick={() => setDrawer({ open: true, subject })} />
            ),
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      {canManage && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer({ open: true })}>
          {t("subjects.create")}
        </Button>
      )}
      {subjects.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void subjects.refetch()}>{t("app.retry")}</Button>} />}
      <Table<Subject>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={subjects.isPending}
        columns={columns}
        dataSource={subjects.data ?? []}
        pagination={false}
        locale={{ emptyText: t("subjects.empty") }}
      />
      <SubjectDrawer
        open={drawer.open}
        subject={drawer.subject}
        onClose={() => setDrawer({ open: false })}
        onSaved={(subject, created) => {
          setDrawer({ open: false });
          void message.success(t(created ? "subjects.created" : "subjects.updated", { name: subject.name }));
        }}
      />
    </Space>
  );
}

function CoursesTab() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { hasPermission } = useAuth();
  const screens = Grid.useBreakpoint();
  const canManage = hasPermission("academic.manage");
  const subjects = useSubjects();
  const [drawer, setDrawer] = useState<{ open: boolean; course?: Course }>({ open: false });

  const courses = useQuery({
    queryKey: ["academic", "courses"],
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/courses");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const columns = [
    { title: t("courses.name"), dataIndex: "name", key: "name", ellipsis: true },
    {
      title: t("courses.allowedGrades"),
      dataIndex: "allowedGrades",
      key: "allowedGrades",
      width: 200,
      render: (allowed: Course["allowedGrades"]) => allowed.map((g) => <Tag key={g}>{g}</Tag>),
    },
    {
      title: t("courses.subjects"),
      key: "subjects",
      responsive: ["md" as const],
      render: (_: unknown, course: Course) => (
        <Space size={[4, 4]} wrap>
          {course.subjects.map((s) => (
            <Tag key={s.subjectId} color={s.kind === "Main" ? "green" : "default"}>
              {s.subjectName}
            </Tag>
          ))}
        </Space>
      ),
    },
    ...(canManage
      ? [
          {
            title: t("common.actions"),
            key: "actions",
            width: 70,
            render: (_: unknown, course: Course) => (
              <Button type="text" icon={<EditOutlined />} aria-label={t("common.edit")} onClick={() => setDrawer({ open: true, course })} />
            ),
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      {canManage && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer({ open: true })}>
          {t("courses.create")}
        </Button>
      )}
      {courses.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void courses.refetch()}>{t("app.retry")}</Button>} />}
      <Table<Course>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={courses.isPending}
        columns={columns}
        dataSource={courses.data ?? []}
        pagination={false}
        locale={{ emptyText: t("courses.empty") }}
        scroll={{ x: 480 }}
      />
      {drawer.open && (
        <CourseDrawer
          open
          course={drawer.course}
          subjects={subjects.data ?? []}
          onClose={() => setDrawer({ open: false })}
          onSaved={(course, created) => {
            setDrawer({ open: false });
            void message.success(t(created ? "courses.created" : "courses.updated", { name: course.name }));
          }}
        />
      )}
    </Space>
  );
}
